import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceCalendar } from '@/components/AbsenceApplication/AbsenceCalendar';
import { Modal } from '@/components/Modal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { usePageScrollLock } from '@/hooks/usePageScrollLock';
import { ModalVariant } from '@/types/components';

interface Props {
  isOpen: boolean;
  filledDate: Date | null;
  outlinedDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  hasFixedWeekCount?: boolean;
  popoverClassName?: string;
  onChange: (date: Date) => void;
  onClose: () => void;
}

export const CalendarSheet: React.FC<Props> = ({
  isOpen,
  filledDate,
  outlinedDate,
  minDate,
  maxDate,
  hasFixedWeekCount = false,
  popoverClassName,
  onChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);
  const popoverRef = useRef<HTMLDivElement>(null);

  usePageScrollLock(isOpen && !isMobile);

  const calendar = (
    <AbsenceCalendar
      filledDate={filledDate}
      outlinedDate={outlinedDate}
      minDate={minDate}
      maxDate={maxDate}
      hasFixedWeekCount={hasFixedWeekCount}
      onChange={onChange}
    />
  );

  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }

    popoverRef.current?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [isOpen, isMobile]);

  if (isMobile) {
    return (
      <Modal
        isOpen={isOpen}
        label={t('global.selectDate')}
        variant={ModalVariant.BottomSheet}
        contentRef={sheetRef}
        onClose={onClose}
      >
        <div
          {...swipeProps}
          className="flex shrink-0 flex-col cursor-grab touch-none active:cursor-grabbing"
        >
          <div className="mx-auto mt-2 h-[3px] w-10 rounded-full bg-drag-handle" />
          <h2 className="px-4 pt-4 text-[24px]/[30px] font-semibold text-foreground">
            {t('global.selectDate')}
          </h2>
        </div>
        <div className="px-4 pt-4 pb-[calc(64px+env(safe-area-inset-bottom))]">
          {calendar}
        </div>
      </Modal>
    );
  }

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default"
      />
      <div
        ref={popoverRef}
        className={`absolute z-50 rounded-2xl bg-card p-4 shadow-modal ${
          popoverClassName ?? ''
        }`}
      >
        {calendar}
      </div>
    </>
  );
};
